import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { Typography, colors, Tab, Grid, Button, Table, Loading } from '@leapeasy/ui-kit';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getInvitePropertyAction, inviteUserAction } from 'store/actions/inviteActions';
import { failedNotif, successNotif } from 'store/actions/notifActions';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { readStoredUser } from 'utils/authSession';
import { InviteComposePanel } from 'containers/User/InviteNewUser/InvitePage/InviteComposePanel';
import { getInviteUserColumns } from 'containers/User/InviteNewUser/InvitePage/InviteTableColumns';
import { InvitePropertySelectAllBar } from 'containers/User/InviteNewUser/InvitePage/InvitePropertySelectAllBar';
import { getInviteProperty } from 'store/sagas/APIs/invite';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { fetchAllPaginated } from 'utils/exportPagination';
import {
  getDelegationInviteTabs,
  isDelegationInviteSingleSelect,
  filterDelegationInviteBuildings,
} from './delegationInviteTabs';
import { mapDelegationInviteTableRow } from '../shared/delegationPropertyTable';
import {
  pageHeaderSx,
  pageTitleStyle,
  contentContainerSx,
  actionRowSx,
  helperColumnSx,
  actionColumnSx,
  helperTextStyle,
  selectedCountStyle,
  composeButtonStyle,
  tableWrapperSx,
} from 'containers/User/InviteNewUser/InvitePage/InvitePage.styles';
import { GlobalUXState } from 'components/GlobalUXState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { useDemoMask } from 'hooks/useDemoMask';

const INVITE_PROPERTY_LIST_FILTER_DEFAULT = {
  searchText: '',
  options: {},
  searchPlaceholder: 'Search Property..',
};

export const DelegationInvitePage = () => {
  const { isDemo } = useDemoMask();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storedUser = readStoredUser();
  const currentUserState = useSelector((state) => state.getIn(['auth', 'currentUser']));
  const currentUser = currentUserState ? currentUserState.toJS() : storedUser;
  const currentUserRole = currentUser?.role;

  const availableTabs = useMemo(() => getDelegationInviteTabs(currentUserRole), [currentUserRole]);

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [openComposer, setOpenComposer] = useState(false);
  const [awaitingInviteResponse, setAwaitingInviteResponse] = useState(false);
  const [sortOptions, setSortOptions] = useState({});
  const [filter, setFilter] = useState(INVITE_PROPERTY_LIST_FILTER_DEFAULT);
  const inviteListTableRef = useRef(null);
  const selectedBuildingCacheRef = useRef(new Map());
  const selectedAllIdsRef = useRef([]);
  const [selectAllState, setSelectAllState] = useState({ scopeKey: null, selected: false });
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [inviteListExportOpen, setInviteListExportOpen] = useState(false);

  const [pagination, setPagination] = useState({
    pageNumber: 0,
    rowsPerPage: 12,
    maxVisiblePageItems: 6,
    onUpdate: (value) => {
      if (value === -1) value = 0;
      setPagination((prev) => ({ ...prev, pageNumber: value }));
    },
    showPageNumberInput: 6,
  });

  const inviteProperties = useSelector((state) => state.getIn(['invite', 'inviteProperty']));
  const loading = useSelector((state) => state.getIn(['auth', 'loading']));
  const { hasSuccess: hasSuccessNotification, hasError: hasErrorNotification } =
    useNotificationMessages();

  const invitePropertiesJSON = inviteProperties ? inviteProperties.toJS() : null;
  const fromEmail = currentUser?.email || '';
  const selectedRole = availableTabs[selectedTab]?.role;
  const singleSelect = isDelegationInviteSingleSelect(selectedRole);
  const selectionScopeKey = `${selectedRole || ''}:${filter.searchText || ''}`;

  useEffect(() => {
    setSelectedProperties([]);
    selectedBuildingCacheRef.current.clear();
    selectedAllIdsRef.current = [];
    setSelectAllState({ scopeKey: null, selected: false });
    setPagination((prev) => ({ ...prev, pageNumber: 0 }));
    setFilter(INVITE_PROPERTY_LIST_FILTER_DEFAULT);
  }, [selectedRole]);

  useEffect(() => {
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [filter.searchText]);

  useEffect(() => {
    if (!selectedRole) return;

    dispatch(
      getInvitePropertyAction({
        inviteRole: selectedRole,
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions,
        filter: { searchText: (filter.searchText || '').trim() },
      }),
    );
  }, [
    selectedRole,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
    filter.searchText,
    dispatch,
  ]);

  const buildingRows = useMemo(
    () => filterDelegationInviteBuildings(invitePropertiesJSON?.data || [], selectedRole),
    [invitePropertiesJSON, selectedRole],
  );
  useEffect(() => {
    buildingRows.forEach((building) => {
      selectedBuildingCacheRef.current.set(building.building_id, building);
    });
  }, [buildingRows]);

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(invitePropertiesJSON?.total) || 0,
    isLoading: Boolean(invitePropertiesJSON?.isLoading),
  });

  const selectedBuildings = useMemo(
    () =>
      selectedProperties
        .map((buildingId) => selectedBuildingCacheRef.current.get(buildingId))
        .filter(Boolean),
    [buildingRows, selectedProperties],
  );

  const suggestedToEmail = useMemo(
    () =>
      selectedRole === 'pm'
        ? selectedBuildings
            .map((item) => item.email_address)
            .filter(Boolean)
            .join(', ')
        : '',
    [selectedBuildings, selectedRole],
  );

  const visibleBuildingIds = useMemo(
    () => buildingRows.map((building) => building.building_id),
    [buildingRows],
  );
  const selectedVisibleCount = useMemo(
    () => visibleBuildingIds.filter((id) => selectedProperties.includes(id)).length,
    [visibleBuildingIds, selectedProperties],
  );
  const allVisibleSelected =
    !singleSelect &&
    visibleBuildingIds.length > 0 &&
    selectedVisibleCount === visibleBuildingIds.length;
  const partiallySelected =
    !singleSelect && selectedVisibleCount > 0 && selectedVisibleCount < visibleBuildingIds.length;
  const allSelected =
    (selectAllState.scopeKey === selectionScopeKey && selectAllState.selected) ||
    allVisibleSelected;

  const onToggleProperty = useCallback(
    (propertyId) => {
      if (
        selectAllState.scopeKey === selectionScopeKey &&
        selectedAllIdsRef.current.includes(propertyId)
      ) {
        setSelectAllState((existing) => ({ ...existing, selected: false }));
      }
      setSelectedProperties((existing) => {
        if (singleSelect) {
          return existing.includes(propertyId) ? [] : [propertyId];
        }
        return existing.includes(propertyId)
          ? existing.filter((id) => id !== propertyId)
          : [...existing, propertyId];
      });
    },
    [selectAllState.scopeKey, selectionScopeKey, singleSelect],
  );

  const onToggleAllProperties = useCallback(() => {
    if (singleSelect || selectAllLoading) return;
    const totalRecords = Number(invitePropertiesJSON?.total) || buildingRows.length;
    if (totalRecords === 0) return;

    const fetchMatchingBuildings = async () => {
      setSelectAllLoading(true);
      try {
        const matchingBuildings = await fetchAllPaginated({
          totalRecords,
          fetchPage: ({ offset, limit }) =>
            getInviteProperty({
              inviteRole: selectedRole,
              offset,
              limit,
              sortOptions,
              filter: { searchText: (filter.searchText || '').trim() },
            }),
        });
        const eligibleBuildings = filterDelegationInviteBuildings(matchingBuildings, selectedRole);
        eligibleBuildings.forEach((building) => {
          selectedBuildingCacheRef.current.set(building.building_id, building);
        });
        const matchingIds = eligibleBuildings.map((building) => building.building_id);
        const deselectingAll =
          selectAllState.scopeKey === selectionScopeKey && selectAllState.selected;
        if (deselectingAll) {
          selectedAllIdsRef.current = [];
          setSelectAllState({ scopeKey: selectionScopeKey, selected: false });
        } else {
          selectedAllIdsRef.current = matchingIds;
          setSelectAllState({ scopeKey: selectionScopeKey, selected: true });
        }
        setSelectedProperties((existing) => {
          if (deselectingAll) {
            return existing.filter((id) => !matchingIds.includes(id));
          }
          return [...new Set([...existing, ...matchingIds])];
        });
      } catch (error) {
        dispatch(failedNotif('Unable to select all matching properties. Please try again.'));
      } finally {
        setSelectAllLoading(false);
      }
    };

    fetchMatchingBuildings();
  }, [
    buildingRows.length,
    dispatch,
    filter.searchText,
    invitePropertiesJSON?.total,
    selectedRole,
    singleSelect,
    selectAllLoading,
    selectAllState,
    selectionScopeKey,
    sortOptions,
  ]);

  const tableData = useMemo(
    () => buildingRows.map((building) => mapDelegationInviteTableRow(building, isDemo)),
    [buildingRows, isDemo],
  );

  const onOpenComposer = () => {
    if (selectedProperties.length === 0) return;
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingInviteResponse(false);
    setOpenComposer(true);
  };

  const onDiscardComposer = () => {
    setAwaitingInviteResponse(false);
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setOpenComposer(false);
  };

  useEffect(() => {
    if (!awaitingInviteResponse || !hasSuccessNotification) return;
    setOpenComposer(false);
    setAwaitingInviteResponse(false);
    setSelectedProperties([]);
    selectedBuildingCacheRef.current.clear();
  }, [awaitingInviteResponse, hasSuccessNotification]);

  useEffect(() => {
    if (awaitingInviteResponse && hasErrorNotification) {
      setAwaitingInviteResponse(false);
    }
  }, [awaitingInviteResponse, hasErrorNotification]);

  const onSendInvite = ({ toEmail, subject, content }) => {
    const recipients = toEmail
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean);

    if (!selectedRole || recipients.length === 0 || selectedProperties.length === 0) {
      return;
    }

    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingInviteResponse(true);
    dispatch(
      inviteUserAction({
        userRole: selectedRole,
        toEmail: recipients,
        emailSubject: subject || "You've been Invited to the Leap Portal",
        emailContent: content || 'You have been invited to the Leap Portal.',
        property: selectedProperties,
      }),
    );
  };

  const onClickBuildingDetail = useCallback(
    (buildingId) => {
      navigate(`/property/properties/${buildingId}/detail/report`);
    },
    [navigate],
  );

  const handleInviteListExportData = useCallback(async () => {
    if (!selectedRole) return [];
    const totalRecords = Number(invitePropertiesJSON?.total) || buildingRows.length || 0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInviteProperty({
            inviteRole: selectedRole,
            offset,
            limit,
            sortOptions,
            filter: { searchText: (filter.searchText || '').trim() },
          }),
      });
      return filterDelegationInviteBuildings(rows, selectedRole).map((building) =>
        mapDelegationInviteTableRow(building, isDemo),
      );
    } catch (e) {
      throw new Error('Unable to export delegation invite properties right now.');
    }
  }, [
    selectedRole,
    invitePropertiesJSON?.total,
    buildingRows.length,
    sortOptions,
    filter.searchText,
    isDemo,
  ]);

  const columns = useMemo(
    () =>
      getInviteUserColumns({
        selectedProperties,
        onToggleProperty,
        onClickInviteUserDetail: onClickBuildingDetail,
        showDelegationColumns: true,
      }),
    [selectedProperties, onToggleProperty, onClickBuildingDetail],
  );

  const shouldShowInitialLoading = Boolean(selectedRole) && !invitePropertiesJSON;
  const hasNoTabs = availableTabs.length === 0;
  const hasSearch = Boolean(`${filter.searchText || ''}`.trim());

  return (
    <DashboardLayoutContainer>
      <InviteComposePanel
        open={openComposer}
        fromEmail={fromEmail}
        suggestedToEmail={suggestedToEmail}
        selectedBuildings={selectedRole === 'pm' ? selectedBuildings : []}
        loading={loading && awaitingInviteResponse}
        disableSend={selectedProperties.length === 0}
        onClose={onDiscardComposer}
        onSend={onSendInvite}
      />

      <Box sx={pageHeaderSx}>
        <Typography variant="h3" style={{ color: colors.purple[900], ...pageTitleStyle }}>
          Invite A New User
        </Typography>
      </Box>

      <Box padding="24px 12px" borderRadius="12px" mt={1} sx={contentContainerSx}>
        {hasNoTabs ? (
          <Typography variant="body2" style={{ color: colors.neutral[700] }}>
            Your account does not have access to delegation invites.
          </Typography>
        ) : (
          <>
            <Typography
              variant="body2"
              style={{ ...helperTextStyle, marginBottom: 16, display: 'block' }}
            >
              Select user level, choose associated properties, then compose and send the invitation
              email.
            </Typography>

            <Grid>
              {availableTabs.map((tab, index) => (
                <Tab
                  key={tab.role}
                  title={tab.title}
                  isSelected={selectedTab === index}
                  onClick={() => setSelectedTab(index)}
                />
              ))}
            </Grid>

            {selectedRole === 'vp' && (
              <Typography
                variant="body2"
                style={{ ...helperTextStyle, marginTop: 12, display: 'block' }}
              >
                Select all properties you want to include in this invitation.
              </Typography>
            )}

            <Box marginTop="20px">
              <Box sx={actionRowSx}>
                <Box sx={helperColumnSx}>
                  <Typography variant="body2" style={helperTextStyle}>
                    Choose associated properties, then click Send Invite.
                  </Typography>
                </Box>
                <Box sx={actionColumnSx}>
                  {selectedProperties.length > 0 && (
                    <>
                      <Typography variant="body2" style={selectedCountStyle}>
                        {selectedProperties.length} row(s) selected
                      </Typography>
                      <Button variant="primary" onClick={onOpenComposer} style={composeButtonStyle}>
                        Send Invite
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={tableWrapperSx}>
                {shouldShowInitialLoading ? (
                  <Loading size="large" label="Loading properties..." />
                ) : (
                  <>
                    {!singleSelect && buildingRows.length > 0 && (
                      <InvitePropertySelectAllBar
                        allSelected={allSelected}
                        partiallySelected={partiallySelected}
                        onToggle={onToggleAllProperties}
                        visibleCount={invitePropertiesJSON?.total || buildingRows.length}
                        hasSearch={hasSearch}
                        loading={selectAllLoading}
                      />
                    )}
                    <Table
                      showEmptyState
                      emptyContent={
                        <GlobalUXState
                          variant={hasSearch ? 'noResults' : 'noData'}
                          compact
                          description={
                            hasSearch
                              ? 'No properties match your search.'
                              : 'There are no properties available to invite for this user level.'
                          }
                        />
                      }
                      filter={filter}
                      onChangeFilter={setFilter}
                      ref={inviteListTableRef}
                      onExportButtonClick={() => setInviteListExportOpen(true)}
                      onRequestExportData={handleInviteListExportData}
                      onRequestFullExportData={handleInviteListExportData}
                      data={tableData}
                      columns={columns}
                      sortOptions={sortOptions}
                      onChangeSort={setSortOptions}
                      pagination={{
                        ...pagination,
                        totalItems: invitePropertiesJSON?.total || buildingRows.length,
                        totalPages: Math.ceil(
                          (invitePropertiesJSON?.total || buildingRows.length) /
                            pagination.rowsPerPage,
                        ),
                      }}
                      onChangeRowsPerPage={(value) =>
                        setPagination({
                          ...pagination,
                          rowsPerPage: value,
                          pageNumber: 0,
                        })
                      }
                      onChangePagination={setPagination}
                    />
                    <ListExportScopeModal
                      open={inviteListExportOpen}
                      onClose={() => setInviteListExportOpen(false)}
                      tableRef={inviteListTableRef}
                      title="Delegation invite properties"
                      currentPageRowCount={tableData.length}
                      paginationTotalItems={invitePropertiesJSON?.total || buildingRows.length}
                      enableFiltered
                      fullDatasetTotal={invitePropertiesJSON?.total || buildingRows.length}
                      enableFull
                      enableSelected={false}
                    />
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </DashboardLayoutContainer>
  );
};
